import TYPES from '@/types';
import Vue from 'vue';

// Application
import GetPersonalInfoQuery
  from '@/modules/flagship/investor-profile/personal-info/application/queries/get-personal-info-query';
import EditRetirementFundGoalPlanRetirementAgeService
  from '@/modules/flagship/edit-plan-goals/edit-retirement-fund-goal-plan/application/services/edit-retirement-fund-goal-plan-retirement-age-service';

// Domain
import {
  CreateInvestorGoalStateManager,
} from '@/modules/flagship/investor-goal/investor_goal/domain/state/create-investor-goal-state-manager';
import Inject from '@/modules/shared/domain/di/inject';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';

export default class RetirementFundAgeViewModel {
  @Inject(TYPES.GET_PERSONAL_INFO_QUERY)
  private readonly get_personal_info_query!: GetPersonalInfoQuery;

  @Inject(TYPES.EDIT_RETIREMENT_FUND_GOAL_PLAN_RETIREMENT_AGE_SERVICE)
  private readonly retirement_age_service!: EditRetirementFundGoalPlanRetirementAgeService;

  @Inject(TYPES.CREATE_INVESTOR_GOAL_STATE_MANAGER)
  readonly create_investor_goal_state_manager!: CreateInvestorGoalStateManager;

  @Inject(TYPES.I18N)
  private readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  readonly i18n_namespace = 'components.goals-dashboard.edit-plan-goals.edit-retirement-fund-goal-plan.edit_retirement_fund_retirement_age';

  readonly view: Vue;

  public constructor(view: Vue) {
    this.view = view;
  }

  readonly min_age_to_retirement = 55;

  readonly max_age_to_retirement = 75;

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  plan_information = [
    {
      label: this.translate('current_age'),
      value: '',
      tooltip: '',
    },
    {
      label: this.translate('retirement'),
      value: '',
      tooltip: '',
    },
    {
      label: this.translate('pension'),
      value: '',
      tooltip: this.translate('pension_tooltip'),
    },
    {
      label: this.translate('life_expectancy'),
      value: '',
      tooltip: '',
    },
  ]

  plan_term_state = this.retirement_age_service.getRetirementAgeInformation();

  investor_goal_state = this.create_investor_goal_state_manager.state;

  initialize = async () => {
    await this.loadRetirementFundInvestorGoalInformation();
  }

  setState = () => {
    this.plan_term_state.retirement = this.plan_term_state.retirement_age
      - this.plan_term_state.current_age;
    this.plan_term_state.pension = this.plan_term_state.lifetime_expected
      - this.plan_term_state.retirement_age;
  }

  setAgeToRetirement = () => {
    this.plan_information[1].value = `${this.plan_term_state.retirement_age - this.plan_term_state.current_age} ${this.translate('years')}`;
    this.plan_information[2].value = `${this.plan_term_state.lifetime_expected - this.plan_term_state.retirement_age} ${this.translate('years')}`;
  }

  getCurrentAge = async () => {
    const { person } = await this.get_personal_info_query.execute(
      this.investor_goal_state.investor_goal.investor_profile_id,
    );
    this.plan_term_state.current_age = person.current_age;
  }

  loadRetirementFundInvestorGoalInformation = async () => {
    if (this.plan_term_state.current_age === 0) {
      await this.getCurrentAge();
    }
    this.plan_term_state.retirement_age = (this.plan_term_state.retirement_age
      < this.min_age_to_retirement) ? this
        .min_age_to_retirement : this.plan_term_state.retirement_age;
    this.plan_term_state.retirement = this.plan_term_state.retirement_age - this.plan_term_state
      .current_age;
    this.plan_term_state.pension = this.plan_term_state.lifetime_expected
      - this.plan_term_state.retirement_age;

    this.plan_information[0].value = `${this.plan_term_state.current_age} ${this.translate('years')}`;
    this.plan_information[1].value = `${this.plan_term_state.retirement} ${this.translate('years')}`;
    this.plan_information[2].value = `${this.plan_term_state.pension} ${this.translate('years')}`;
    this.plan_information[3].value = `${this.plan_term_state.lifetime_expected} ${this.translate('years')}`;
  }

  prevStep = () => {
    this.view.$emit('prevStep');
  }

  nextStep = async () => {
    try {
      this.investor_goal_state.is_loading = true;
      this.setState();
      this.view.$emit('nextStep');
      this.investor_goal_state.is_loading = false;
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.save'));
    }
  }
}
